import { useState, useEffect } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import {
  getOrganization,
  updateOrganization,
  deleteOrganization,
  getMembers,
  removeMember,
  getInvites,
  generateInvite,
  revokeInvite,
  getErrorMessage,
  getOrganizationStats,
  useInvite,
} from '../../api/organizationApi';
import { Organization, Member, Invite, OrganizationStats } from '../../types/organization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';

export function OrganizationSettingsScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const id = currentScreen.params?.organizationId as string;

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  const user = useAuthStore((state) => state.user);
  const switchToOrganization = useAuthStore((state) => state.switchToOrganization);

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      // Load core data (org, members, stats) — accessible to all members
      const [org, orgMembers, orgStats] = await Promise.all([
        getOrganization(id),
        getMembers(id),
        getOrganizationStats(id),
      ]);
      setOrganization(org);
      setMembers(orgMembers);
      setStats(orgStats);
      setName(org.name);

      // Load invites separately — only owner has access (403 for others)
      try {
        const orgInvites = await getInvites(id);
        setInvites(orgInvites);
      } catch {
        // Not an owner/admin — no access to invites, that's OK
        setInvites([]);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !name.trim()) return;

    try {
      setSaving(true);
      setError(null);
      const updated = await updateOrganization(id, { name: name.trim() });
      setOrganization(updated);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Название организации обновлено');
      } else {
        alert('Название организации обновлено');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!organization) return;
    if (members.length > 1) {
      setError(`Невозможно удалить организацию. Сначала удалите всех участников (кроме владельца). Текущее количество: ${members.length}`);
      return;
    }
    setShowDeleteConfirm(true);
  };

  const performDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      setError(null);
      setShowDeleteConfirm(false);
      await deleteOrganization(id);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Организация удалена', () => {
          popScreen();
          popScreen();
        });
      } else {
        alert('Организация удалена');
        popScreen();
        popScreen();
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      if (errorMessage.includes('with members')) {
        setError('Невозможно удалить организацию с участниками. Сначала удалите всех участников.');
      } else if (errorMessage.includes('permissions')) {
        setError('Только владелец может удалить организацию');
      } else {
        setError(errorMessage);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (!id) return;

    const confirmMessage = `Удалить участника ${userName} из организации?`;

    let confirmed = false;
    if (window.Telegram?.WebApp?.showConfirm) {
      confirmed = await new Promise<boolean>((resolve) => {
        window.Telegram!.WebApp!.showConfirm(confirmMessage, resolve);
      });
    } else {
      confirmed = window.confirm(confirmMessage);
    }

    if (!confirmed) return;

    try {
      await removeMember(id, userId);
      await loadData();
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Участник удален');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleGenerateInvite = async () => {
    if (!id) return;
    try {
      setGeneratingInvite(true);
      setError(null);
      await generateInvite(id, { role: 'admin', valid_days: 7, max_uses: 10 });
      await loadData();
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Приглашение создано');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setGeneratingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string, inviteCode: string) => {
    if (!id) return;

    const confirmMessage = `Отозвать приглашение ${inviteCode}?`;

    let confirmed = false;
    if (window.Telegram?.WebApp?.showConfirm) {
      confirmed = await new Promise<boolean>((resolve) => {
        window.Telegram!.WebApp!.showConfirm(confirmMessage, resolve);
      });
    } else {
      confirmed = window.confirm(confirmMessage);
    }

    if (!confirmed) return;

    try {
      await revokeInvite(id, inviteId);
      await loadData();
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Приглашение отозвано');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Код скопирован');
      } else {
        alert('Код скопирован');
      }
    });
  };

  const copyInviteLink = (code: string) => {
    const deepLink = `https://t.me/ProCoach_AI_bot?start=org_invite_${code}`;
    navigator.clipboard.writeText(deepLink).then(() => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Ссылка скопирована');
      } else {
        alert('Ссылка скопирована');
      }
    });
  };

  const handleJoinByCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const code = joinCode.trim().toUpperCase();
    if (!code) {
      setError('Введите код приглашения');
      return;
    }

    const codePattern = /^ORG-[A-Z0-9]{8}-[A-Z0-9]{8}$/;
    if (!codePattern.test(code)) {
      setError('Неверный формат кода. Ожидается: ORG-XXXXXXXX-XXXXXXXX');
      return;
    }

    try {
      setJoining(true);
      const result = await useInvite({ code });
      await switchToOrganization(result.organization.id);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(
          `Вы присоединились к организации "${result.organization.name}"`,
          () => window.location.reload(),
        );
      } else {
        alert(`Вы присоединились к организации "${result.organization.name}"`);
        window.location.reload();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Не удалось присоединиться';
      if (msg.includes('already a member')) {
        setError('Вы уже являетесь участником этой организации');
      } else if (msg.includes('expired') || msg.includes('revoked')) {
        setError('Приглашение истекло или было отозвано');
      } else if (msg.includes('limit')) {
        setError('Лимит участников организации достигнут');
      } else {
        setError(msg);
      }
    } finally {
      setJoining(false);
    }
  };

  const formatLimit = (value: number | null | undefined) => {
    if (value === null || value === undefined || value === -1) return '∞';
    return value;
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', color: 'var(--text-hint)', marginBottom: '4px',
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  if (!organization) {
    return (
      <div className="p-4 space-y-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--danger)' }}>{error || 'Организация не найдена'}</p>
        </Card>
        <Button onClick={() => popScreen()} variant="secondary" fullWidth>&larr; Назад</Button>
      </div>
    );
  }

  const currentMember = members.find((m) => m.user_id === user?.id);
  const isOwner = currentMember?.role === 'owner';
  const canDelete = isOwner && members.length === 1;
  const activeInvites = invites.filter((i) => !i.revoked_at);
  const maxMembers = stats?.limits.max_members ?? 1;
  const isTeamPlan = maxMembers > 1;

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Настройки"
        subtitle={organization.name}
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
        </Card>
      )}

      {/* Edit Name — only for owner */}
      {isOwner && (
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Основные настройки</h3>
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <label style={labelStyle}>Название организации</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={saving || deleting}
              required
              style={inputStyle}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>Максимум 200 символов</p>
          </div>
          <Button
            onClick={() => {}}
            disabled={saving || deleting || name === organization.name || !name.trim()}
            fullWidth
          >
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </Button>
        </form>
      </Card>
      )}

      {/* Subscription */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Подписка</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Тариф:</span>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{organization.subscription_tier.toUpperCase()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: 'var(--text-hint)' }}>Статус:</span>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {organization.subscription_status === 'trial' && 'Триал'}
              {organization.subscription_status === 'active' && 'Активна'}
              {organization.subscription_status === 'expired' && 'Истекла'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: 'var(--text-hint)' }}>
              {organization.subscription_expires_at ? 'Действует до:' : 'Срок действия:'}
            </span>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {organization.subscription_expires_at
                ? new Date(organization.subscription_expires_at).toLocaleDateString('ru-RU')
                : 'Бессрочно'}
            </span>
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={() => pushScreen('subscription-manage')} variant="secondary" fullWidth>
            Управление подпиской
          </Button>
        </div>
      </Card>

      {/* Join Organization by Code */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Присоединиться к организации
        </h3>
        <form onSubmit={handleJoinByCode} className="space-y-3">
          <div>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ORG-XXXXXXXX-XXXXXXXX"
              disabled={joining}
              maxLength={21}
              style={{
                ...inputStyle,
                fontFamily: 'monospace',
                textAlign: 'center',
                letterSpacing: '1px',
              }}
            />
            <p className="text-xs mt-1 text-center" style={{ color: 'var(--text-hint)' }}>
              Введите код приглашения от администратора организации
            </p>
          </div>
          <Button
            onClick={() => {}}
            disabled={joining || !joinCode.trim()}
            fullWidth
          >
            {joining ? 'Подключение...' : 'Присоединиться'}
          </Button>
        </form>
      </Card>

      {/* Members — показываем только для командных тарифов (max_members > 1) */}
      {isTeamPlan && (
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Участники ({members.length} из {formatLimit(stats?.limits.max_members)})
        </h3>
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.user_id}
              className="flex items-center justify-between p-2 rounded-lg"
              style={{ border: '1px solid var(--separator)' }}
            >
              <div className="flex items-center gap-2">
                <span>{member.role === 'owner' ? '👑' : '⭐'}</span>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {member.user_name || 'Unnamed User'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                    {member.role === 'owner' ? 'Владелец' : 'Админ'}
                  </p>
                </div>
              </div>
              {isOwner && member.role !== 'owner' && (
                <button
                  onClick={() => handleRemoveMember(member.user_id, member.user_name || 'этого участника')}
                  disabled={saving || deleting}
                  className="text-sm px-2"
                  style={{ color: 'var(--danger)' }}
                >
                  X
                </button>
              )}
            </div>
          ))}
        </div>
      </Card>
      )}

      {/* Invites — показываем только для командных тарифов */}
      {isOwner && isTeamPlan && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Приглашения</h3>

          <Button
            onClick={handleGenerateInvite}
            disabled={generatingInvite || deleting}
            fullWidth
          >
            {generatingInvite ? 'Создание...' : 'Создать приглашение'}
          </Button>

          {activeInvites.length > 0 ? (
            <div className="space-y-2 mt-3">
              {activeInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="p-3 rounded-lg"
                  style={{ border: '1px solid var(--separator)' }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-mono font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{invite.code}</p>
                    <div className="flex gap-1">
                      <button
                        onClick={() => copyInviteCode(invite.code)}
                        disabled={deleting}
                        className="text-sm px-1"
                        title="Копировать код"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => copyInviteLink(invite.code)}
                        disabled={deleting}
                        className="text-sm px-1"
                        title="Копировать ссылку"
                      >
                        🔗
                      </button>
                      <button
                        onClick={() => handleRevokeInvite(invite.id, invite.code)}
                        disabled={deleting}
                        className="text-sm px-1"
                        style={{ color: 'var(--danger)' }}
                      >
                        X
                      </button>
                    </div>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                    Роль: {invite.role === 'admin' ? 'Админ' : invite.role}
                    {' \u00b7 '}
                    Использований: {invite.current_uses}/{invite.max_uses || '\u221E'}
                    {invite.expires_at && (
                      <>
                        {' \u00b7 '}
                        Истекает: {new Date(invite.expires_at).toLocaleDateString('ru-RU')}
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            !generatingInvite && (
              <p className="text-center py-3 mt-2" style={{ color: 'var(--text-hint)' }}>Нет активных приглашений</p>
            )
          )}
        </Card>
      )}

      {/* Danger Zone — only for owner */}
      {isOwner && (
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--danger)' }}>Опасная зона</h3>
        <p className="text-sm mb-3" style={{ color: 'var(--text-hint)' }}>
          {canDelete
            ? 'Это действие нельзя отменить. Все данные организации будут удалены.'
            : 'Чтобы удалить организацию, сначала удалите всех участников.'}
        </p>
        <Button
          onClick={handleDelete}
          disabled={!canDelete || deleting || saving}
          variant="danger"
          fullWidth
        >
          {deleting ? 'Удаление...' : 'Удалить организацию'}
        </Button>
      </Card>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center" onClick={() => setShowDeleteConfirm(false)}>
          <div className="mx-4 rounded-xl p-6 max-w-sm w-full" style={{ backgroundColor: 'var(--bg-primary)' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[17px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Удалить организацию?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-hint)' }}>
              Организация "{organization.name}" и все её данные будут удалены безвозвратно.
            </p>
            <div className="flex gap-2">
              <Button variant="danger" onClick={performDelete} fullWidth>Удалить</Button>
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} fullWidth>Отмена</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
